import { useEffect, useState } from 'react';
import { apiJson } from './api';

const CACHE_KEY = 'gh_viewer_country';
// sessionStorage stores strings. Map "null" -> JS null on the way back out
// so we can distinguish "fetched and unknown" from "not yet fetched".
const SENTINEL_NULL = '__null__';

interface GeoResponse {
  status: number;
  data: { country: string | null };
}

interface ViewerCountryState {
  country: string | null;
  loading: boolean;
}

function readCache(): string | null | undefined {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === null) return undefined;
    return cached === SENTINEL_NULL ? null : cached;
  } catch {
    return undefined;
  }
}

/**
 * Synchronous read of the cached viewer country (ISO 3166-1 alpha-2) for
 * code paths that can't use the React hook — currently:
 *   - consent.ts:syncDeel, which gates DEEL loading on US-only and runs
 *     synchronously inside saveConsent()
 *   - the inline IIFE in web/index.html (which inlines the same logic
 *     directly against sessionStorage, but follows the same contract)
 *
 * Returns the ISO code if known, `null` if known-unknown (geo fetch
 * resolved with no country) OR if not yet fetched / blocked / weird env.
 * Callers should treat null as "not US" — fail-closed on the US gate.
 */
export function readCachedViewerCountry(): string | null {
  const cached = readCache();
  return cached === undefined ? null : cached;
}

function writeCache(country: string | null): void {
  try {
    sessionStorage.setItem(CACHE_KEY, country === null ? SENTINEL_NULL : country);
  } catch {
    /* sessionStorage blocked — accept the next-page re-fetch */
  }
}

/**
 * Fetch + cache the viewer's country (ISO 3166-1 alpha-2). The API derives
 * it from the CloudFront-Viewer-Country header (see api/src/util/geo.ts).
 *
 * Cached for the browser session so the non-US notice on /rules and any
 * future geo-gated UI doesn't flicker on navigation.
 *
 * Returns:
 *  - `country: 'US' | 'CA' | …`  — known viewer country
 *  - `country: null`             — couldn't determine (dev, missing CF
 *                                  header, or fetch failure). Treat as
 *                                  "unknown — don't gate on geo".
 *  - `loading: true`             — first session fetch in flight
 *
 * Dev override: append `?country=XX` to the SPA URL. The hook forwards the
 * value to the API which echoes it back. Lets you test the non-US banner
 * locally without CloudFront in front of the API.
 */
export function useViewerCountry(): ViewerCountryState {
  const initialCache = typeof window !== 'undefined' ? readCache() : undefined;
  const [country, setCountry] = useState<string | null>(initialCache ?? null);
  const [loading, setLoading] = useState(initialCache === undefined);

  useEffect(() => {
    if (initialCache !== undefined) return;
    let cancelled = false;

    // Forward ?country=XX from the SPA URL to /geo. The API only honors it
    // in non-production so a prod viewer can't fake their location through
    // this path.
    const params = new URLSearchParams(window.location.search);
    const override = params.get('country');
    const path = override ? `/geo?country=${encodeURIComponent(override)}` : '/geo';

    apiJson<GeoResponse>(path)
      .then((res) => {
        if (cancelled) return;
        setCountry(res.data.country);
        writeCache(res.data.country);
      })
      .catch(() => {
        if (cancelled) return;
        setCountry(null);
        writeCache(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialCache]);

  return { country, loading };
}
